import { useMemo, useState } from "react";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { Link } from "react-router-dom";
import {
  FileText, ClipboardCheck, Printer, ArrowRight, CheckCircle2, Circle,
  Info, ShieldCheck, GraduationCap, Briefcase, Heart, Plane, Award, Building2,
  Users, Globe, AlertCircle,
} from "lucide-react";

/**
 * Document Checklist Tool — original StudyNet implementation.
 * Visa categories and document group names are publicly available
 * Department of Home Affairs taxonomy; descriptions written fresh by StudyNet.
 */

const VISA_OPTIONS = [
  { id: "student-500", label: "Student Visa (Subclass 500)", icon: GraduationCap },
  { id: "graduate-485", label: "Temporary Graduate Visa (Subclass 485)", icon: GraduationCap },
  { id: "guardian-590", label: "Student Guardian Visa (Subclass 590)", icon: Users },
  { id: "visitor-600", label: "Visitor Visa (Subclass 600)", icon: Plane },
  { id: "partner-309-820", label: "Partner Visa (309 / 100 · 820 / 801)", icon: Heart },
  { id: "skilled-189-190-491", label: "Skilled Visas (189 / 190 / 491)", icon: Award },
  { id: "skills-in-demand-482", label: "Skills in Demand Visa (Subclass 482)", icon: Briefcase },
  { id: "ens-186", label: "Employer Nomination Scheme (Subclass 186)", icon: Building2 },
  { id: "training-407", label: "Training Visa (Subclass 407)", icon: Briefcase },
  { id: "business-188", label: "Business Innovation & Investment (Subclass 188)", icon: Building2 },
  { id: "citizenship", label: "Citizenship (Conferral / Descent)", icon: Globe },
];

// Document groups used across visa categories. Each entry: {key, title, items[]}
// All items are common, factual document references used by DHA.
const GROUPS = {
  identity: { title: "Identity & passport",
    items: ["Valid passport (full colour copy of bio-data page)", "Passport-style photograph (recent, name on back)", "Birth certificate or family registry", "National identity document (where applicable)", "Change-of-name documents (deed poll / marriage / divorce)"] },
  english: { title: "English language",
    items: ["IELTS Academic / General test result (within validity period)", "PTE Academic test result", "TOEFL iBT result", "OET result (medical occupations)", "Cambridge C1 Advanced (CAE) result", "Evidence of study in English (where exemption applied)"] },
  health: { title: "Health",
    items: ["BUPA / Medibank / nib OSHC certificate (Student / Guardian)", "HBF / Allianz / nib OVHC certificate (where applicable)", "Health examination results (when requested via HAP ID)", "Chest X-ray report (where required by country grade)", "Specialist medical reports for any declared condition"] },
  character: { title: "Character",
    items: ["National Police Certificate for every country lived in 12+ months in the past 10 years", "Australian Federal Police (AFP) National Police Check", "Statutory declaration declaring any prior criminal history", "Military service records (where applicable)"] },
  finance: { title: "Financial capacity",
    items: ["Bank statements (last 3–6 months)", "Term deposits / fixed deposits with recognised institutions", "Loan letter (from approved education / commercial lender)", "Sponsor bank statements & income evidence", "Tax returns (own / sponsor) for the past 2 financial years", "Source-of-funds declaration / explanation"] },
  studyEnrol: { title: "Course & enrolment",
    items: ["Confirmation of Enrolment (CoE) for each course", "Letter of offer from CRICOS provider", "Pre-paid tuition receipts (where applicable)", "Academic transcripts and certificates (originals + translations)", "Statement of Purpose / Genuine Student responses"] },
  workSkills: { title: "Work & skills",
    items: ["Detailed CV / resume (in reverse-chronological format)", "Skills assessment from relevant assessing authority", "Employment reference letters (with duties, dates, hours, salary)", "Payslips and tax / superannuation evidence (Australian roles)", "Contracts for current and past employment", "Position description and organisational chart for sponsored role"] },
  sponsor: { title: "Sponsor / employer / nominator",
    items: ["Employer nomination approval (where applicable)", "Australian business registration (ABN / ACN / ASIC extracts)", "Financial statements / profit & loss / BAS for the past 2 years", "Annual Market Salary Rate (AMSR) evidence", "Labour Market Testing evidence (where required)", "Standard Business Sponsorship approval letter"] },
  partner: { title: "Relationship evidence (Partner / Family)",
    items: ["Marriage certificate or evidence of de facto relationship (12+ months)", "Joint financial documents — accounts, leases, utilities", "Joint household evidence — mail, shared address, rental agreement", "Statements from family and friends (Form 888)", "Photographs across the relationship timeline", "Communication records during separations / Form 80 history"] },
  business: { title: "Business & investment",
    items: ["Personal & business net-asset valuation reports", "Audited financial statements of nominated business(es)", "Shareholding register and ownership evidence", "Business plan tailored to the chosen state / territory", "Investment portfolio statements (where applicable)", "Tax compliance certificates from home country"] },
  family: { title: "Family members",
    items: ["Birth certificates of dependent children", "Custody / guardianship documents (where applicable)", "Marriage / de facto evidence (for secondary applicants)", "Form 80 / Form 1221 for adult members", "Health and character documents for each family member"] },
  citizenship: { title: "Citizenship-specific",
    items: ["Citizenship application form (1300t / 118 / 1290)", "Evidence of permanent residence (for conferral)", "Movement records / international travel history", "Australian Defence Force service records (where applicable)", "Two referee statements"] },
};

// Mapping visa → ordered list of document group keys
const VISA_DOC_MAP = {
  "student-500":          ["identity", "english", "health", "character", "finance", "studyEnrol", "family"],
  "graduate-485":         ["identity", "english", "health", "character", "studyEnrol", "workSkills", "family"],
  "guardian-590":         ["identity", "english", "health", "character", "finance", "studyEnrol", "family"],
  "visitor-600":          ["identity", "health", "character", "finance"],
  "partner-309-820":      ["identity", "health", "character", "finance", "partner", "family"],
  "skilled-189-190-491":  ["identity", "english", "health", "character", "workSkills", "family"],
  "skills-in-demand-482": ["identity", "english", "health", "character", "workSkills", "sponsor", "family"],
  "ens-186":              ["identity", "english", "health", "character", "workSkills", "sponsor", "family"],
  "training-407":         ["identity", "english", "health", "character", "workSkills", "sponsor", "family"],
  "business-188":         ["identity", "english", "health", "character", "finance", "business", "family"],
  "citizenship":          ["identity", "character", "citizenship"],
};

export default function DocumentChecklistTool() {
  const [visa, setVisa] = useState("student-500");
  const [checked, setChecked] = useState({});

  const groups = VISA_DOC_MAP[visa] || [];
  const totals = useMemo(() => {
    let total = 0, done = 0;
    groups.forEach((g) => {
      GROUPS[g].items.forEach((_, i) => {
        const key = `${visa}::${g}::${i}`;
        total += 1;
        if (checked[key]) done += 1;
      });
    });
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [visa, groups, checked]);

  const toggle = (g, i) => {
    const key = `${visa}::${g}::${i}`;
    setChecked((c) => ({ ...c, [key]: !c[key] }));
  };

  const reset = () => setChecked({});
  const handlePrint = () => window.print();

  return (
    <div>
      <PageHeader
        eyebrow="Tools & Assessments"
        title="Document Checklist Tool."
        subtitle="Select your visa, tick off documents as you collect them, then print or save your checklist. Indicative only — every application has additional case-specific evidence."
        image="/assets/graduate.jpg"
      />

      <section data-testid="checklist-tool" className="py-14 md:py-20 bg-[#F8F9FA] print:bg-white print:py-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Visa selector */}
            <aside className="lg:col-span-4 print:hidden">
              <div className="bg-white border border-slate-200 p-6 sticky top-28">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-2">
                  <ClipboardCheck className="h-3.5 w-3.5" /> Step 1 — choose your visa
                </div>
                <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
                  {VISA_OPTIONS.map((v) => {
                    const Icon = v.icon;
                    const active = v.id === visa;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        data-testid={`visa-opt-${v.id}`}
                        onClick={() => setVisa(v.id)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors border ${
                          active
                            ? "bg-[#29324A] border-[#29324A] text-white"
                            : "border-slate-200 hover:border-[#C43A89] text-[#29324A]"
                        }`}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-[#29324A]"}`} />
                        <span>{v.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Progress + actions */}
                <div className="mt-5 pt-5 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2 text-[11px] tracking-wider uppercase text-slate-500 font-bold">
                    <span>Progress</span>
                    <span data-testid="checklist-progress">{totals.done} / {totals.total} · {totals.pct}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 border border-slate-200">
                    <div className="h-full bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89] transition-all duration-500" style={{ width: `${totals.pct}%` }} />
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button
                      type="button"
                      onClick={handlePrint}
                      data-testid="checklist-print"
                      className="inline-flex items-center justify-center gap-1.5 bg-[#29324A] hover:bg-[#1f2638] text-white text-[10px] font-bold tracking-wider uppercase py-2.5 transition-colors"
                    >
                      <Printer className="h-3.5 w-3.5" /> Print / save
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      data-testid="checklist-reset"
                      className="inline-flex items-center justify-center gap-1.5 border border-slate-300 hover:border-[#C43A89] text-[#29324A] text-[10px] font-bold tracking-wider uppercase py-2.5 transition-colors"
                    >
                      Reset ticks
                    </button>
                  </div>
                </div>
              </div>
            </aside>

            {/* Checklist body */}
            <div className="lg:col-span-8 space-y-5">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1 flex items-center gap-2 print:text-black">
                <FileText className="h-3.5 w-3.5" /> Step 2 — collect your documents
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight">
                Documents for {VISA_OPTIONS.find((v) => v.id === visa)?.label}
              </h2>

              {groups.map((g) => {
                const grp = GROUPS[g];
                return (
                  <div key={g} data-testid={`group-${g}`} className="bg-white border border-slate-200 p-5 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <h3 className="font-display text-lg text-[#29324A] leading-snug">{grp.title}</h3>
                      <span className="text-[10px] font-bold tracking-[0.18em] text-[#29324A] uppercase">
                        {grp.items.length} items
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {grp.items.map((item, i) => {
                        const key = `${visa}::${g}::${i}`;
                        const isChecked = !!checked[key];
                        return (
                          <li key={i}>
                            <button
                              type="button"
                              data-testid={`item-${g}-${i}`}
                              onClick={() => toggle(g, i)}
                              className={`w-full text-left flex items-start gap-3 px-3 py-2 transition-colors ${
                                isChecked ? "bg-[#F0FDF4]" : "hover:bg-[#F8F4FB]"
                              }`}
                            >
                              {isChecked ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                              ) : (
                                <Circle className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />
                              )}
                              <span className={`text-[13px] leading-relaxed ${isChecked ? "text-slate-500 line-through" : "text-slate-700"}`}>
                                {item}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

              {/* CTA */}
              <div className="bg-[#29324A] grain text-white p-6 md:p-7 relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1.5">Need help with documents?</div>
                  <h3 className="font-display text-lg md:text-xl leading-snug">Our team can review your evidence pack before lodgement.</h3>
                </div>
                <Link
                  to="/book-consultation"
                  data-testid="checklist-book-cta"
                  className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer"
                >
                  Book consultation <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <Disclaimer>
                This Document Checklist Tool is general guidance only. Every visa application has additional case-specific evidence requirements determined by the Department of Home Affairs. Always confirm the current document list at <a href="https://immi.homeaffairs.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">immi.homeaffairs.gov.au</a> or with a Registered Migration Agent before lodging.
              </Disclaimer>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Confused about which documents you actually need? Talk to our team." />
    </div>
  );
}
